import React, { useEffect, useMemo, useState } from 'react';
import BillingCategoryService from '@frontend/AppSettings/services/BillingCategoryService';
import { BillingCategory } from '@frontend/Models/Rate/RateModels';
import './CostCodeBudgetInputs.css';

interface CostCodeBudgetInputsProps {
  /** code -> hours (string, so an empty field stays empty rather than 0). Controlled by the parent. */
  values: Record<string, string>;
  onChange: (code: string, value: string) => void;
}

/**
 * A controlled grid of per-cost-code hour inputs for the create-project/matter flows. Rows come from
 * the ACTIVE billing_categories catalog (data-driven — a newly added code shows up automatically).
 * Leaving a code blank stores nothing (uncapped, per Phase 1's "no positive budget → never capped").
 */
const CostCodeBudgetInputs: React.FC<CostCodeBudgetInputsProps> = ({ values, onChange }) => {
  const [categories, setCategories] = useState<BillingCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Overall project budget is the sum of the per-code entries; blank/invalid fields count as 0.
  const total = useMemo(
    () =>
      Object.values(values).reduce((sum, raw) => {
        const n = parseFloat(String(raw));
        return Number.isFinite(n) && n > 0 ? sum + n : sum;
      }, 0),
    [values],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await BillingCategoryService.getCategories(true);
        if (!cancelled) setCategories(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <div className="cost-code-budget-inputs-empty">Loading cost codes…</div>;
  }
  if (categories.length === 0) {
    return (
      <div className="cost-code-budget-inputs-empty">
        No cost codes configured yet. Add codes under Team → Cost Codes.
      </div>
    );
  }

  return (
    <div className="cost-code-budget-inputs">
      <div className="cost-code-budget-inputs-hint">
        Optional — leave a code blank to keep it uncapped.
      </div>
      <div className="cost-code-budget-inputs-scroll">
        <table className="cost-code-budget-inputs-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Description</th>
              <th className="cost-code-budget-inputs-hrs-col">Hrs</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <tr key={cat.categoryid}>
                <td className="cost-code-budget-inputs-code">{cat.categoryid}</td>
                <td className="cost-code-budget-inputs-desc">{cat.category}</td>
                <td className="cost-code-budget-inputs-hrs-col">
                  <input
                    type="number"
                    min="0"
                    step="any"
                    className="cost-code-budget-inputs-field"
                    placeholder="—"
                    value={values[cat.categoryid] ?? ''}
                    onChange={(e) => onChange(cat.categoryid, e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="cost-code-budget-inputs-total">
        <span>Total Project Budget</span>
        <span className="cost-code-budget-inputs-total-value">
          {total.toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs
        </span>
      </div>
    </div>
  );
};

export default CostCodeBudgetInputs;
